import getWeatherForecastOperation from 'owa-service/lib/operation/getWeatherForecastOperation';
import getWeatherForecastRequest from 'owa-service/lib/factory/getWeatherForecastRequest';
import type GetWeatherForecastResponse from 'owa-service/lib/contract/GetWeatherForecastResponse';
import weatherLocationId from 'owa-service/lib/factory/weatherLocationId';
import { getJsonRequestHeader } from 'owa-service/lib/ServiceRequestUtils';
import { makeGetRequest } from 'owa-ows-gateway';
import { isFeatureEnabled } from 'owa-feature-flags';
import { convertToGetWeatherForecastResponse } from './utils/convertToGetWeatherForecastResponse';
import { getWeatherServiceUrl } from './utils/getWeatherServiceUrl';
import convertOwsWeatherForecastToGqlWeatherForecast from './utils/convertOwsWeatherForecastToGqlWeatherForecast';
import convertGqlTemperatureUnitToOwsTemperatureUnit from './utils/convertGqlTemperatureUnitToOwsTemperatureUnit';
import type {
    WeatherLocation as GqlWeatherLocation,
    TemperatureUnit as GqlTemperatureUnit,
    WeatherForecast,
    QueryWeatherForecastArgs,
    ResolverContext,
} from 'owa-graph-schema';

/**
 * Query Web resolver responsible for getting the weather forecast
 */
export const weatherForecastQueryWeb = async (
    parent: any,
    args: QueryWeatherForecastArgs,
    context: ResolverContext,
    info: any
): Promise<WeatherForecast> => {
    const { temperatureUnit, weatherLocations } = args;

    const owsWeatherForecastResponse: GetWeatherForecastResponse | null = await getWeatherForecastService(
        temperatureUnit,
        weatherLocations
    );

    if (owsWeatherForecastResponse == null || owsWeatherForecastResponse.ErrorMessage) {
        const errorMessage = owsWeatherForecastResponse
            ? owsWeatherForecastResponse.ErrorMessage
            : 'getWeatherForecastService response is null';
        throw new Error(
            `weatherForecastQueryWeb: failed to get the weather forecast. Error: ${errorMessage}`
        );
    }

    return convertOwsWeatherForecastToGqlWeatherForecast(owsWeatherForecastResponse);
};

/**
 * Executes getWeatherForecastOperation
 * @param {TemperatureUnit} unit temperature units
 * @param {WeatherLocationId[]} weatherLocationIds list of weather location ids
 * @returns GetWeatherForecastResponse
 */
async function getWeatherForecastService(
    unit: GqlTemperatureUnit,
    weatherLocationIds?: GqlWeatherLocation[]
): Promise<GetWeatherForecastResponse | null> {
    if (weatherLocationIds) {
        return isFeatureEnabled('cal-mf-shouldUseOWSPrimeWeatherApi')
            ? getNewWeatherService(unit, weatherLocationIds)
            : getOldWeatherService(unit, weatherLocationIds);
    }

    return null;
}

async function getOldWeatherService(
    unit: GqlTemperatureUnit,
    weatherLocations: GqlWeatherLocation[]
): Promise<GetWeatherForecastResponse> {
    return getWeatherForecastOperation({
        Header: getJsonRequestHeader(),
        Body: getWeatherForecastRequest({
            WeatherLocationIds: weatherLocations
                ? weatherLocations.map(location => {
                      return weatherLocationId({
                          Longitude: Number(location!.Longitude),
                          Latitude: Number(location!.Latitude),
                          Name: location!.Name as string,
                          EntityId: location!.LocationId as string,
                      });
                  })
                : [],
            TemperatureUnit: convertGqlTemperatureUnitToOwsTemperatureUnit(unit),
        }),
    }).then(response => {
        return response.Body;
    });
}

async function getNewWeatherService(
    unit: GqlTemperatureUnit,
    weatherLocations: GqlWeatherLocation[]
): Promise<GetWeatherForecastResponse> {
    return makeGetRequest(getWeatherServiceUrl(unit, weatherLocations)).then(response => {
        response.WeatherLocations?.map(value => convertToGetWeatherForecastResponse(value));
        return response;
    });
}
